import { Components, createTheme, PaletteOptions, Theme, ThemeOptions } from "@mui/material"
import { TypographyOptions } from "@mui/material/styles/createTypography"
import { IRootState } from "app/model"
import { getStore } from "app/store/store"
import { deepMerge } from "app/tools/common"
import { IUIConfiguration } from "ui/configuration/model"
import { getThemeOptionsWithConfiguration } from "ui/configuration/tools/theme"
import {
  DARK_THEME_BORDER,
  DARK_THEME_BOX_SHADOW,
  DARK_THEME_ERROR,
  DARK_THEME_FORM,
  DARK_THEME_FORM_TEXT,
  DARK_THEME_PALETTE_BACKGROUND_DEFAULT,
  DARK_THEME_PALETTE_BACKGROUND_PAPER,
  DARK_THEME_PALETTE_PRIMARY_DARK_COLOR,
  DARK_THEME_PALETTE_PRIMARY_LIGHT_COLOR,
  DARK_THEME_PALETTE_SECONDARY_COLOR,
  DARK_THEME_TEXT_PRIMARY,
  DARK_THEME_TEXT_PRIMARY2,
  DARK_THEME_TEXT_SECONDARY,
  LIGHT_THEME_BORDER,
  LIGHT_THEME_BOX_SHADOW,
  LIGHT_THEME_ERROR,
  LIGHT_THEME_FORM,
  LIGHT_THEME_FORM_TEXT,
  LIGHT_THEME_PALETTE_BACKGROUND_DEFAULT,
  LIGHT_THEME_PALETTE_BACKGROUND_PAPER,
  LIGHT_THEME_PALETTE_BORDER_DISABLED_COLOR,
  LIGHT_THEME_PALETTE_PRIMARY_DARK_COLOR,
  LIGHT_THEME_PALETTE_SECONDARY_COLOR,
  LIGHT_THEME_TEXT_PRIMARY,
  LIGHT_THEME_TEXT_PRIMARY2,
  LIGHT_THEME_TEXT_SECONDARY,
  MAP_COLOR,
  THEME_PALETTE_PRIMARY_COLOR,
  THEME_PALETTE_SPECIAL_TEXT_COLOR,
  UI_THEMES
} from "ui/model"
import { uiActionCreator } from "ui/store/actions"
import { isLoggedIn } from "user/tools/common"
import { isSystemAdmin } from "user/tools/permissions"
import { saveUserUiPreferences } from "./common"

/**
 * See ./model.ts for custom Palette properties
 */

const sharedComponentOptions: Components<Omit<Theme, "components">> = {
  MuiAutocomplete: {
    styleOverrides: {
      option: {
        fontSize: ".85rem"
      },
      noOptions: {
        fontSize: ".85rem"
      },
      loading: {
        fontSize: ".85rem"
      }
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: ".85rem"
      }
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        "& input": {
          fontSize: ".8rem"
        }
      }
    }
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        "& .MuiFormLabel-root.MuiInputLabel-root": {
          fontSize: ".8rem"
        },
        "& .MuiSelect-select.MuiInputBase-input": {
          fontSize: ".8rem"
        },
        "& .MuiInputBase-root": {
          minHeight: "2.5rem"
        },
        ".form & .MuiInputBase-root.MuiOutlinedInput-root.Mui-focused fieldset": {
          borderWidth: "1px"
        }
      }
    }
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        "& input": {
          fontSize: ".8rem"
        }
      }
    }
  },
  MuiButton: {
    styleOverrides: {
      root: {
        "paddingTop": "0 !important",
        "paddingBottom": "0 !important",
        "paddingLeft": "2rem !important",
        "paddingRight": "2rem !important",
        "height": "2.5rem",
        "lineHeight": "2.5rem",
        "fontSize": ".875rem !important",
        "textTransform": "initial",
        "fontWeight": "normal",
        "borderRadius": ".125rem",
        "whiteSpace": "nowrap",
        "&:hover": {
          backgroundColor: THEME_PALETTE_SPECIAL_TEXT_COLOR,
          color: THEME_PALETTE_PRIMARY_COLOR
        }
      },
      outlined: {
        "paddingTop": "0 !important",
        "paddingBottom": "0 !important",
        "&:hover": {
          backgroundColor: MAP_COLOR,
          color: THEME_PALETTE_SPECIAL_TEXT_COLOR
        }
      },
      text: {
        "&:hover": {
          backgroundColor: MAP_COLOR,
          color: THEME_PALETTE_SPECIAL_TEXT_COLOR
        },
        "color": THEME_PALETTE_PRIMARY_COLOR
      }
    }
  }
}

const getComponentsOverrides = (
  toMergeOptions: Components<Omit<Theme, "components">>
): Components<Omit<Theme, "components">> => deepMerge(sharedComponentOptions, toMergeOptions)

const sharedPaletteOptions: Partial<PaletteOptions> = {
  specialText: THEME_PALETTE_SPECIAL_TEXT_COLOR
}

const getPaletteOverrides = (toMergeOptions: Partial<PaletteOptions>): PaletteOptions =>
  deepMerge(sharedPaletteOptions, toMergeOptions)

const sharedTypographyOptions: TypographyOptions = {
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700
}

const defaultLightThemeOptions: ThemeOptions = {
  components: getComponentsOverrides({
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          color: LIGHT_THEME_TEXT_PRIMARY
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "color": LIGHT_THEME_TEXT_PRIMARY2,
          "&.Mui-checked, &.MuiCheckbox-indeterminate": {
            color: LIGHT_THEME_TEXT_PRIMARY2
          },
          "&.Mui-disabled": {
            color: LIGHT_THEME_PALETTE_BORDER_DISABLED_COLOR
          }
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          ".MuiSwitch-colorPrimary.Mui-checked": {
            color: LIGHT_THEME_TEXT_PRIMARY2
          },
          ".MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
            backgroundColor: LIGHT_THEME_TEXT_PRIMARY2
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& fieldset": {
            boxShadow: "0 4px 12px -4px rgba(0, 0, 0, 0.12)",
            border: LIGHT_THEME_BORDER
          }
        }
      }
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          ".form & .MuiInputBase-root.MuiOutlinedInput-root.Mui-focused fieldset": {
            borderColor: LIGHT_THEME_FORM
          },
          ".form & .MuiFormLabel-root.MuiInputLabel-root.Mui-focused": {
            color: LIGHT_THEME_FORM
          },
          "& .MuiFormControlLabel-label": {
            color: LIGHT_THEME_TEXT_PRIMARY
          }
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          "& input": {
            backgroundColor: THEME_PALETTE_SPECIAL_TEXT_COLOR
          }
        }
      }
    }
  }),
  palette: getPaletteOverrides({
    type: UI_THEMES.LIGHT,
    border: LIGHT_THEME_BORDER,
    formText: LIGHT_THEME_FORM_TEXT,
    boxShadow: LIGHT_THEME_BOX_SHADOW,
    primary: {
      main: THEME_PALETTE_PRIMARY_COLOR,
      dark: LIGHT_THEME_PALETTE_PRIMARY_DARK_COLOR
    },
    secondary: {
      main: LIGHT_THEME_PALETTE_SECONDARY_COLOR
    },
    background: {
      default: LIGHT_THEME_PALETTE_BACKGROUND_DEFAULT,
      paper: LIGHT_THEME_PALETTE_BACKGROUND_PAPER
    },
    action: {
      disabled: LIGHT_THEME_PALETTE_BORDER_DISABLED_COLOR,
      disabledBackground: "#f5f5f5"
    },
    text: {
      primary: LIGHT_THEME_TEXT_PRIMARY,
      secondary: LIGHT_THEME_TEXT_SECONDARY,
      primary2: LIGHT_THEME_TEXT_PRIMARY2,
      disabled: LIGHT_THEME_PALETTE_BORDER_DISABLED_COLOR
    },
    error: {
      main: LIGHT_THEME_ERROR
    }
  }),
  typography: sharedTypographyOptions
}

const defaultDarkThemeOptions: ThemeOptions = {
  components: getComponentsOverrides({
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          color: DARK_THEME_TEXT_PRIMARY
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          ".MuiSwitch-colorPrimary": {
            color: DARK_THEME_PALETTE_SECONDARY_COLOR
          },
          ".MuiSwitch-colorPrimary.Mui-checked": {
            color: DARK_THEME_TEXT_PRIMARY2
          },
          ".MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
            backgroundColor: DARK_THEME_TEXT_PRIMARY2
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& fieldset": {
            boxShadow: "0 4px 12px -4px rgba(0, 0, 0, 60%)",
            border: DARK_THEME_BORDER
          }
        }
      }
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          ".form & .MuiInputBase-root.MuiOutlinedInput-root.Mui-focused fieldset": {
            borderColor: DARK_THEME_FORM
          },
          ".form & .MuiFormLabel-root.MuiInputLabel-root.Mui-focused": {
            color: DARK_THEME_FORM
          },
          "& .MuiFormControlLabel-label": {
            color: DARK_THEME_TEXT_PRIMARY
          },
          "& .Mui-disabled": {
            color: `${DARK_THEME_PALETTE_SECONDARY_COLOR} !important`
          },
          ".form & .MuiInputBase-root.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "black"
          },
          "& .MuiInputBase-root.MuiOutlinedInput-root.Mui-disabled:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: DARK_THEME_PALETTE_SECONDARY_COLOR
          }
        }
      }
    }
  }),
  palette: getPaletteOverrides({
    type: UI_THEMES.DARK,
    border: DARK_THEME_BORDER,
    form: DARK_THEME_FORM,
    formText: DARK_THEME_FORM_TEXT,
    boxShadow: DARK_THEME_BOX_SHADOW,
    primary: {
      main: THEME_PALETTE_PRIMARY_COLOR,
      light: DARK_THEME_PALETTE_PRIMARY_LIGHT_COLOR,
      dark: DARK_THEME_PALETTE_PRIMARY_DARK_COLOR
    },
    secondary: {
      main: DARK_THEME_TEXT_SECONDARY,
      dark: "#4d4e5a"
    },
    background: {
      default: DARK_THEME_PALETTE_BACKGROUND_DEFAULT,
      paper: DARK_THEME_PALETTE_BACKGROUND_PAPER
    },
    action: {
      disabled: DARK_THEME_PALETTE_SECONDARY_COLOR,
      disabledBackground: "#4d4e5a",
      active: THEME_PALETTE_PRIMARY_COLOR
    },
    text: {
      primary: DARK_THEME_TEXT_PRIMARY,
      secondary: DARK_THEME_PALETTE_SECONDARY_COLOR,
      primary2: DARK_THEME_TEXT_PRIMARY2,
      disabled: DARK_THEME_PALETTE_SECONDARY_COLOR
    },
    error: {
      main: DARK_THEME_ERROR
    }
  }),
  typography: sharedTypographyOptions
}

export const getDefaultTheme = (): Theme =>
  createTheme(isUserBrowserIsDarkMode() ? defaultDarkThemeOptions : defaultLightThemeOptions)

export function setTheme(useDarkTheme: boolean, config?: IUIConfiguration): void {
  if (useDarkTheme === undefined) {
    useDarkTheme = true
  }
  if (isDarkThemeActive() === useDarkTheme && !config) {
    return
  }
  if (!config) {
    config = getStore().getState().ui.config
  }
  const defaultThemeOptions = useDarkTheme ? defaultDarkThemeOptions : defaultLightThemeOptions
  getStore().dispatch(
    uiActionCreator.setTheme(
      createTheme(getThemeOptionsWithConfiguration(useDarkTheme, defaultThemeOptions, config)),
      config
    )
  )
  if (!isLoggedIn()) {
    return
  }
  if (!isSystemAdmin()) {
    saveUserUiPreferences({ theme: useDarkTheme ? UI_THEMES.DARK : UI_THEMES.LIGHT })
  }
}

export function isUserBrowserIsDarkMode(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

export const isDarkThemeActive = (state?: IRootState): boolean =>
  ((state || getStore().getState()).ui.theme as any).palette.type === UI_THEMES.DARK
